import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  PlatformActionDetail,
  PlatformActionPage,
  PlatformActionRequest,
} from '../models/platformAction.types'
import type { PlatformActionError } from '../models/platformActionError'
import { DEFAULT_PAGE_SIZE } from '../service/pageParams'

export type PlatformActionLoadStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface PlatformActionFilters {
  /** Wire status value, or `''` for "all". */
  status: string
  /** Wire platform value, or `''` for "all". */
  platform: string
}

export function emptyFilters(): PlatformActionFilters {
  return { status: '', platform: '' }
}

/**
 * Pure state container for the platform-actions surface. No HTTP and no flow imports — the flows
 * drive it (contract: `component → composable → store → service → api`).
 *
 * `loadedKey` is the same race guard `anomalyStore` uses: a filter change or a workspace switch
 * makes an older in-flight response stale, and only the current key may write.
 */
export const usePlatformActionStore = defineStore('platformActions', () => {
  // ── List ───────────────────────────────────────────────────────────
  const requests = ref<PlatformActionRequest[]>([])
  const page = ref(1)
  const pageSize = ref(DEFAULT_PAGE_SIZE)
  const total = ref(0)
  const filters = ref<PlatformActionFilters>(emptyFilters())
  const status = ref<PlatformActionLoadStatus>('idle')
  const error = ref<PlatformActionError | null>(null)
  const loadedKey = ref<string | null>(null)

  // ── Detail (drawer + polling) ──────────────────────────────────────
  const detail = ref<PlatformActionDetail | null>(null)
  const detailStatus = ref<PlatformActionLoadStatus>('idle')
  const detailError = ref<PlatformActionError | null>(null)

  const isEmpty = computed(() => status.value === 'ready' && requests.value.length === 0)
  const pendingApprovalCount = computed(
    () => requests.value.filter((request) => request.status === 'pending_approval').length,
  )

  function isCurrent(key: string): boolean {
    return loadedKey.value === key
  }

  function setLoading(key: string): void {
    loadedKey.value = key
    status.value = 'loading'
    error.value = null
  }

  function setPage(key: string, next: PlatformActionPage): boolean {
    if (!isCurrent(key)) return false
    requests.value = next.requests
    page.value = next.page
    // A zero page_size means the backend omitted it; keep the requested size rather than showing 0.
    if (next.pageSize > 0) pageSize.value = next.pageSize
    total.value = next.total
    status.value = 'ready'
    error.value = null
    return true
  }

  function setError(key: string, next: PlatformActionError): boolean {
    if (!isCurrent(key)) return false
    requests.value = []
    total.value = 0
    error.value = next
    status.value = 'error'
    return true
  }

  function setFilters(next: PlatformActionFilters): void {
    filters.value = next
    page.value = 1
  }

  function setPageNumber(next: number): void {
    page.value = next
  }

  function setPageSize(next: number): void {
    pageSize.value = next
    page.value = 1
  }

  // ── Detail writers ─────────────────────────────────────────────────
  function setDetailLoading(): void {
    detailStatus.value = 'loading'
    detailError.value = null
  }

  function setDetail(next: PlatformActionDetail): void {
    detail.value = next
    detailStatus.value = 'ready'
    detailError.value = null
    patchRequest(next.request)
  }

  function setDetailError(next: PlatformActionError): void {
    detailError.value = next
    detailStatus.value = 'error'
  }

  function clearDetail(): void {
    detail.value = null
    detailStatus.value = 'idle'
    detailError.value = null
  }

  /**
   * Replace one request in place after a decision or a poll tick, so the table's status column and
   * the open drawer both track it without a full re-list.
   *
   * The two halves are independent: a request absent from the current page is simply not in the
   * table (it arrives with the next list load), but the drawer may still be showing it — a poll
   * tick after the user filtered or paged away must keep updating that drawer.
   */
  function patchRequest(next: PlatformActionRequest): void {
    const index = requests.value.findIndex((request) => request.id === next.id)
    if (index !== -1) {
      requests.value = requests.value.map((request, at) => (at === index ? next : request))
    }
    if (detail.value && detail.value.request.id === next.id) {
      detail.value = { ...detail.value, request: next }
    }
  }

  function reset(): void {
    requests.value = []
    page.value = 1
    pageSize.value = DEFAULT_PAGE_SIZE
    total.value = 0
    filters.value = emptyFilters()
    status.value = 'idle'
    error.value = null
    loadedKey.value = null
    clearDetail()
  }

  return {
    requests,
    page,
    pageSize,
    total,
    filters,
    status,
    error,
    loadedKey,
    detail,
    detailStatus,
    detailError,
    isEmpty,
    pendingApprovalCount,
    isCurrent,
    setLoading,
    setPage,
    setError,
    setFilters,
    setPageNumber,
    setPageSize,
    setDetailLoading,
    setDetail,
    setDetailError,
    clearDetail,
    patchRequest,
    reset,
  }
})

export type PlatformActionStore = ReturnType<typeof usePlatformActionStore>
